import * as React from 'react'
import { Row, Col, Switch, message, Typography, Drawer, Icon } from 'antd'
import { inject, observer } from 'mobx-react'
import { CurrentCompanyStore } from '../../../../stores/CurrentCompanyStore'
import { parseErrorMsg } from '../../../../utilities/helpers'
import {
  ILocationWithMetaDataContract,
  API,
  ICompanyWithMetaDataContract,
  GiftCardType,
  IGiftCardContract,
  MetaDataType,
} from '@getgreenline/homi-shared'
import Helmet from 'react-helmet'
import { Track } from '../../../../Track'
import { SmartImage } from '../../../../components/SmartImage'
import { CardWrapper } from '../../../../components/CardWrapper'
import { Routes } from '../../../../constants/Routes'
import { BirchmountConfig } from './Birchmount/BirchmountConfig'
import { BLAZE_COMPANY } from '../../../../constants/general'
import { HelpLinks } from '../../../../constants/ExternalLinks'

const { Paragraph, Text } = Typography

interface Props {
  currentCompanyStore?: CurrentCompanyStore
}
interface State {
  windowWidth: number
  isDrawerOpen: boolean
  isLoadingGiftCards: boolean
  mappedGiftCards: Map<GiftCardType, IGiftCardContract>
  companyWithMetaData?: ICompanyWithMetaDataContract<MetaDataType.STRING>
  locationsWithMetaData: Array<ILocationWithMetaDataContract<MetaDataType.INTEGER>>
}

@inject('currentCompanyStore')
@observer
export class GiftCardSettings extends React.Component<Props, State> {
  MIN_WIDTH = 320

  MAX_WIDTH = 400

  companyId = this.props.currentCompanyStore!.company!.id

  constructor(props: Props) {
    super(props)

    this.state = {
      windowWidth: window.innerWidth,
      isDrawerOpen: false,
      isLoadingGiftCards: false,
      mappedGiftCards: new Map(),
      companyWithMetaData: undefined,
      locationsWithMetaData: [],
    }
  }

  componentDidMount = async () => {
    this.updateWindowDimensions()
    window.addEventListener('resize', this.updateWindowDimensions)

    if (this.state.locationsWithMetaData.length === 0) {
      await this.getLocationWithMetaData()
    }

    await this.getCompanyWithMetaData()

    await this.loadMappedGiftCards()
  }

  componentWillUnmount = () => {
    window.removeEventListener('resize', this.updateWindowDimensions)
  }

  updateWindowDimensions = () => this.setState({ windowWidth: window.innerWidth })

  loadMappedGiftCards = async () => {
    const { mappedGiftCards } = this.state

    try {
      this.setState({ isLoadingGiftCards: true })

      const giftCards = await API.getGiftCardsByCompany(this.companyId)

      giftCards.forEach((mappedGiftCard) =>
        mappedGiftCards.set(mappedGiftCard.giftCardTypeId, mappedGiftCard),
      )

      this.setState({ mappedGiftCards })
    } catch (error) {
      message.error(parseErrorMsg(error) || 'An error occurred while loading gift cards')
    } finally {
      this.setState({ isLoadingGiftCards: false })
    }
  }

  updatedMappedGiftCards = async (
    giftCardType: GiftCardType,
    giftCardContract: IGiftCardContract,
  ): Promise<IGiftCardContract> => {
    const updatedGiftCard = await this.props.currentCompanyStore!.updateGiftCard(
      giftCardType,
      giftCardContract,
    )

    const { mappedGiftCards } = this.state

    mappedGiftCards.set(updatedGiftCard.giftCardTypeId, updatedGiftCard)

    this.setState({ mappedGiftCards })

    return updatedGiftCard
  }

  getLocationWithMetaData = async () => {
    try {
      const locationsWithMetaData = await API.getLocations(this.companyId, true)
      this.setState({ locationsWithMetaData })
    } catch (error) {
      message.error(parseErrorMsg(error) || 'An error occurred while loading locations')
    }
  }

  getCompanyWithMetaData = async () => {
    try {
      const companyWithMetaData = await API.getCompanyById(this.companyId, true)
      this.setState({ companyWithMetaData })
    } catch (error) {
      message.error(parseErrorMsg(error) || 'An error occurred while loading company detail')
    }
  }

  toggleIsGiftCardEnabled = async (giftCardType: GiftCardType) => {
    const giftCardContract = this.state.mappedGiftCards.get(giftCardType) || {
      isGiftCardEnabled: false,
      giftCardTypeId: giftCardType,
    }

    giftCardContract.isGiftCardEnabled = !giftCardContract.isGiftCardEnabled

    try {
      this.setState({ isLoadingGiftCards: true })

      const updatedGiftCard = await this.updatedMappedGiftCards(giftCardType, giftCardContract)

      if (updatedGiftCard.isGiftCardEnabled) {
        message.success(
          <span>
            <span>
              Gift cards enabled. Click{' '}
              <a
                href={Routes.DASHBOARD_INVENTORY_BULK_UPDATE(
                  this.companyId,
                  `?searchQuery=${updatedGiftCard.productId}`,
                )}
                target='_blank'
                rel='noreferrer'
              >
                here
              </a>{' '}
              to set inventory
            </span>
          </span>,
          10,
        )

        Track.track('dashboard:settings_giftCardsEnabled', {
          giftCardTypeId: giftCardContract.giftCardTypeId,
        })
      } else {
        message.success('Gift card disabled')

        Track.track('dashboard:settings_giftCardsDisabled', {
          giftCardTypeId: giftCardContract.giftCardTypeId,
        })
      }

      return updatedGiftCard
    } catch (error) {
      message.error(parseErrorMsg(error) || 'An error occurred while changing the giftcard status')
    } finally {
      this.setState({ isLoadingGiftCards: false })
    }
  }

  get drawerWidth() {
    if (this.state.windowWidth <= this.MIN_WIDTH) {
      return this.MIN_WIDTH
    } else if (this.MIN_WIDTH < this.state.windowWidth && this.state.windowWidth < this.MAX_WIDTH) {
      return this.state.windowWidth
    }

    return this.MAX_WIDTH
  }

  render() {
    const {
      isDrawerOpen,
      isLoadingGiftCards,
      mappedGiftCards,
      companyWithMetaData,
      locationsWithMetaData,
    } = this.state

    const merrcoDataCandy = mappedGiftCards.get(GiftCardType.Merrco)
    const birchmount = mappedGiftCards.get(GiftCardType.Birchmount)

    return (
      <Row>
        <Helmet>
          <title>Settings | Gift Cards</title>
          <meta name='page-loaded' content='true' />
        </Helmet>

        <Row className='p-3 white-rounded-container'>
          <Row className='d-flex flex-column'>
            <Row className='d-flex mb-2 justify-content-center'>
              <SmartImage
                src={require('../../../../images/giftcards/img-gr-gift-card.png').default}
                element='div'
              />
            </Row>

            <Typography className='text-center'>
              <Text style={{ fontSize: 24 }}>{BLAZE_COMPANY} gift cards</Text>
              <Paragraph className='mt-2 mb-0'>
                <div>
                  Integrating gift card payments with {BLAZE_COMPANY} provide a faster, more
                  accurate and secure checkout.
                </div>
                <div>
                  <b>Once enabled, you must add inventory before selling gift cards.</b>
                </div>
              </Paragraph>
            </Typography>
          </Row>
        </Row>

        <Row type='flex' style={{ marginRight: '-1rem' }}>
          <CardWrapper
            image={
              <SmartImage
                className='no-minimum-dimension'
                src={require('../../../../images/giftcards/paystone.png').default}
              />
            }
            header={
              <>
                <div className='d-flex'>
                  <b style={{ flexGrow: 1, fontSize: '16px' }}>Merrco gift card</b>
                </div>

                <i className='ml-0 caption'>
                  powered by{' '}
                  <a href='https://www.datacandy.com/' target='_blank' rel='noreferrer'>
                    PayStone
                  </a>
                </i>
              </>
            }
            body={
              <Typography.Paragraph className='mt-2 mb-2'>
                <ul>
                  <li>
                    This setting enables the gift card payment method and add a product called{' '}
                    <i>Merrco gift card</i>.
                  </li>
                  <li>
                    Learn more about Merrco gift cards{' '}
                    <a href={HelpLinks.MERRCO_PAYSTONE_GIFT_CARDS} target='_blank' rel='noreferrer'>
                      here
                    </a>
                    .
                  </li>
                </ul>
              </Typography.Paragraph>
            }
            footer={
              <>
                <Row className='d-flex justify-content-between'>
                  <Col span={20}>Enable Merrco gift card</Col>
                  <Col span={4} className='text-right align-items-center'>
                    {isLoadingGiftCards ? (
                      <Icon
                        type='loading'
                        style={{
                          fontSize: 22,
                          verticalAlign: 'middle',
                        }}
                      />
                    ) : (
                      <Switch
                        checked={merrcoDataCandy?.isGiftCardEnabled}
                        onChange={() => this.toggleIsGiftCardEnabled(GiftCardType.Merrco)}
                      />
                    )}
                  </Col>
                </Row>

                {merrcoDataCandy?.isGiftCardEnabled && (
                  <Row>
                    <span className='ml-0 caption'>
                      <a
                        href={Routes.DASHBOARD_INVENTORY_BULK_UPDATE(
                          this.companyId,
                          `?searchQuery=${merrcoDataCandy?.productId}`,
                        )}
                        target='_blank'
                        rel='noreferrer'
                      >
                        Click
                      </a>{' '}
                      here to set the gift card inventory
                    </span>
                  </Row>
                )}
              </>
            }
          />

          <CardWrapper
            image={
              <SmartImage
                className='no-minimum-dimension'
                src={require('../../../../images/giftcards/birchmount.png').default}
              />
            }
            header={
              <>
                <div className='d-flex'>
                  <b style={{ flexGrow: 1, fontSize: '16px' }}>Birchmount gift card</b>
                </div>

                <i className='ml-0 caption'>
                  powered by{' '}
                  <a href='https://www.birchmountnetwork.com/' target='_blank' rel='noreferrer'>
                    Birchmount
                  </a>
                </i>
              </>
            }
            body={
              <Typography.Paragraph className='mt-2 mb-2'>
                <ul>
                  <li>
                    This setting enables the gift card payment method and add a product called{' '}
                    <i>Birchmount gift card</i>.
                  </li>
                  <li>
                    Learn more about Birchmount gift cards{' '}
                    <a href={HelpLinks.BIRCHMOUNT_GIFT_CARDS} target='_blank' rel='noreferrer'>
                      here
                    </a>
                    .
                  </li>
                </ul>
              </Typography.Paragraph>
            }
            footer={
              <>
                <Row className='d-flex justify-content-between'>
                  <Col span={20}>Enable Birchmount gift card</Col>
                  <Col span={4} className='text-right align-items-center'>
                    {isLoadingGiftCards ? (
                      <Icon
                        type='loading'
                        style={{
                          fontSize: 22,
                          verticalAlign: 'middle',
                        }}
                      />
                    ) : (
                      <Switch
                        checked={birchmount?.isGiftCardEnabled}
                        onChange={async () => {
                          const updatedGiftCard = await this.toggleIsGiftCardEnabled(
                            GiftCardType.Birchmount,
                          )

                          if (updatedGiftCard?.isGiftCardEnabled) {
                            this.setState({ isDrawerOpen: true })
                          }
                        }}
                      />
                    )}
                  </Col>
                </Row>

                {birchmount?.isGiftCardEnabled && (
                  <>
                    <Row>
                      <span className='ml-0 caption'>
                        <a
                          href={Routes.DASHBOARD_INVENTORY_BULK_UPDATE(
                            this.companyId,
                            `?searchQuery=${birchmount?.productId}`,
                          )}
                          target='_blank'
                          rel='noreferrer'
                        >
                          Click
                        </a>{' '}
                        here to set the gift card inventory
                      </span>
                    </Row>

                    <Row>
                      <a
                        className='ml-0 caption'
                        onClick={() => this.setState({ isDrawerOpen: true })}
                      >
                        Configurations
                      </a>
                    </Row>
                  </>
                )}
              </>
            }
          />
        </Row>

        <Drawer
          visible={isDrawerOpen}
          title={`Gift card configurations`}
          width={this.drawerWidth}
          onClose={() => this.setState({ isDrawerOpen: false })}
        >
          {companyWithMetaData !== undefined && (
            <BirchmountConfig
              companyWithMetaData={companyWithMetaData}
              locationsWithMetaData={locationsWithMetaData}
            />
          )}
        </Drawer>
      </Row>
    )
  }
}
